import { Check, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { cva } from 'class-variance-authority';

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/slw/shared/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/slw/shared/ui/popover';
import { Button } from '@/slw/shared/ui/button';
import { cn } from '@/slw/shared/lib/utils';

interface Option {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: Option[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultText?: string;
  disabled?: boolean;
  className?: string;
  popoverClassName?: string;
  size?: 'sm' | 'md' | 'lg';
  color?: 'white' | 'grey';
}

export const Combobox: React.FC<ComboboxProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  noResultText = 'No options found.',
  popoverClassName,
  disabled,
  className,
  size = 'md',
  color = 'white',
  ...props
}) => {
  const [open, setOpen] = React.useState(false);
  const handleSelect = (selectedValue: string) => {
    onChange && onChange?.(selectedValue);
    setSelectedValue(selectedValue);
    setOpen(false);
  };
  const [selectedValue, setSelectedValue] = React.useState(value);

  React.useEffect(() => {
    if (selectedValue !== value) {
      setSelectedValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const comboboxVariants = cva(
    [
      'w-full  rounded-md text-base border border-input font-[400] px-3 py-2 transition-all justify-between',
      'focus:outline-none focus:ring-0 relative',
      'hover:border-default',
      'transition-all duration-200 ease-in-out',
    ],
    {
      variants: {
        size: {
          sm: 'h-10',
          md: 'h-13.5',
          lg: 'h-16',
        },
        color: {
          white: 'bg-secondary hover:bg-gray-input',
          grey: 'bg-gray-input hover:bg-secondary',
        },
      },
      defaultVariants: {
        size: 'md',
        color: 'white',
      },
    },
  );

  const comboboxItemVariants = cva('cursor-pointer pl-6 pr-2 flex items-center text-base transition-colors', {
    variants: {
      size: {
        sm: 'h-10',
        md: 'h-12',
        lg: 'h-16',
      },
      color: {
        white: 'bg-secondary hover:bg-gray-input',
        grey: 'bg-gray-input hover:bg-secondary',
      },
    },
    defaultVariants: {
      size: 'md',
      color: 'white',
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          disabled={disabled}
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn(
            comboboxVariants({ size, color }),
            open ? 'border-[#140C07]' : 'border-[#e6e6e6]',
            value ? 'text-foreground' : 'text-muted-foreground',
            disabled && '[&_svg]:pointer-events-none',
            className,
          )}
        >
          <span>{selectedValue ? options.find((opt) => opt.value === selectedValue)?.label : placeholder}</span>
          <ChevronsUpDown className='h-5 w-5 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[var(--radix-popover-trigger-width)] p-0 border-[#e6e6e6] bg-white', popoverClassName)}
      >
        <Command
          filter={(value, search, keywords = []) => {
            const extendValue = value + ' ' + keywords?.join(' ');

            if (extendValue?.toLowerCase().includes(search?.toLowerCase())) return 1;

            return 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} className={cn('h-9')} disabled={disabled} {...props} />
          <CommandList>
            <CommandEmpty>{noResultText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.value} ${option.label}`}
                  onSelect={() => handleSelect(option.value === selectedValue ? '' : option.value)}
                  className={comboboxItemVariants({ size, color })}
                >
                  {option.label}
                  <Check
                    className={cn('ml-auto size-4', selectedValue === option.value ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
